export type StopLocation = {
	id: string;
	location: string;
	postalCode: string;
	country: string;
	state: string;
	city: string;
	longitude: number;
	latitude: number;
};

export type Stop = {
	id: string;
	stopType: "Pickup" | "Delivery" | undefined;
	stopDate: Date | null;
	location: StopLocation | undefined;
	locationOptions?: StopLocation[];
	orderIndex: number | undefined;
	appointmentInformation?: string;
	stopNumber?: string;
	shipperName?: string;
	streetAddress?: string;
};

function getDefaultStopDate(): Date {
	const now = new Date();

	const easternParts = new Intl.DateTimeFormat("en-US", {
		timeZone: "America/New_York",
		year: "numeric",
		month: "numeric",
		day: "numeric",
		hour: "numeric",
		hour12: false,
	}).formatToParts(now);

	const getPart = (type: string): number =>
		Number(easternParts.find((part) => part.type === type)?.value);

	const year = getPart("year");
	const month = getPart("month");
	const day = getPart("day");
	const hour = getPart("hour");

	// Before 2pm Eastern => today
	// 2pm Eastern or later => tomorrow
	const dayOffset = hour < 14 ? 0 : 1;

	// Build a local Date for the chosen calendar day.
	// If your date picker cares only about the date portion, this is usually fine.
	return new Date(year, month - 1, day + dayOffset);
}

export function createDefaultStops(): Stop[] {
	const defaultStopDate = getDefaultStopDate();

	return [
		{
			id: crypto.randomUUID().toString(),
			stopType: "Pickup",
			stopDate: defaultStopDate,
			location: undefined,
			appointmentInformation: "",
			stopNumber: "",
			shipperName: "",
			streetAddress: "",
			orderIndex: undefined,
		},
		{
			id: crypto.randomUUID().toString(),
			stopType: "Delivery",
			stopDate: defaultStopDate,
			location: undefined,
			appointmentInformation: "",
			stopNumber: "",
			shipperName: "",
			streetAddress: "",
			orderIndex: undefined,
		},
	] as Stop[];
}

/**
 * Re-anchors a `Date`'s local calendar day (year/month/day, as the user sees it in the
 * date picker) to UTC midnight, producing a `Date` whose serialized form
 * (`JSON.stringify` → `Date.prototype.toJSON` → `toISOString`) always encodes that same
 * calendar day, regardless of the browser/OS's configured timezone offset.
 *
 * Without this, `JSON.stringify` converts a local-midnight `Date` to UTC using the
 * machine's own offset. For any timezone ahead of UTC (e.g. UTC+8), that conversion
 * rolls the encoded calendar day back by one day, which can silently turn a
 * correctly-selected pickup/stop date into a past date once it reaches the API.
 */
export function toTimezoneSafeDate(date: Date): Date {
	return new Date(
		Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
	);
}

export function createNewStop(): Stop {
	return {
		id: crypto.randomUUID().toString(),
		stopType: undefined,
		stopDate: getDefaultStopDate(),
		location: undefined,
		orderIndex: undefined,
		appointmentInformation: "",
		stopNumber: "",
		shipperName: "",
		streetAddress: "",
	};
}
